/**
 * src/lib/queries.js — Shared TanStack Query hooks for Affixly Flow.
 *
 * Centralizes query keys + fetchers so multiple components reading the same
 * endpoint share a single cache entry. Re-navigating to a page within the
 * 30s staleTime (configured in App.js) returns instantly from cache.
 *
 * Mutations should call queryClient.invalidateQueries({ queryKey: [...] }) on
 * success to mark cached data stale and trigger background refetch.
 */
import { useQuery } from '@tanstack/react-query';
import api from '../api';

// ── Query keys (single source of truth) ──────────────────────────────────────
// Prefer arrays so we can invalidate by prefix.
export const qk = {
  articles:          () => ['articles'],
  article:           (id) => ['article', id],
  wallpapers:        () => ['wallpapers'],
  freshdeskFolders:  () => ['integrations', 'freshdesk', 'folders'],
  confluenceSpaces:  () => ['integrations', 'confluence', 'spaces'],
  jiraTickets:       (params) => ['jira', 'tickets', params || {}],
  jiraTicket:        (id) => ['jira', 'ticket', id],
  supportStatus:     () => ['support', 'status'],
  supportClusters:   (status) => ['support', 'clusters', status || 'open'],
  brandStyle:        () => ['brand', 'style'],
  brandVoice:        () => ['brand', 'voice'],
  brandOnboarded:    () => ['brand', 'onboarded'],
  detailLevel:       () => ['brand', 'detail-level'],
  settings:          () => ['settings'],
  workspace:         () => ['workspace'],
  freshdeskConfig:   () => ['integrations', 'freshdesk', 'config'],
  providerMode:      () => ['provider-connections', 'mode'],
  whisperCheck:      () => ['whisper-check'],
  authMe:            () => ['auth', 'me'],
  sites:             () => ['sites'],
  site:              (id) => ['site', id],
  siteMembers:       (id) => ['site', id, 'members'],
  searchGaps:        (days) => ['sites', 'search-gaps', { days: days || 30 }],
  onboardingStatus:  () => ['onboarding', 'status'],
  plan:              () => ['plan'],
  aiKeyRouting:      () => ['ai-keys', 'routing'],
  reviews:           (projectSlug) => projectSlug ? ['reviews', { project: projectSlug }] : ['reviews'],
  reviewProjects:    () => ['review-projects'],
  reviewProject:     (slug) => ['review-project', slug],
};

// ── Hooks ────────────────────────────────────────────────────────────────────

export function useArticles(options = {}) {
  return useQuery({
    queryKey: qk.articles(),
    queryFn: async () => {
      const r = await api.get('/articles');
      const d = r.data;
      if (Array.isArray(d)) return d;
      if (d?.articles && Array.isArray(d.articles)) return d.articles;
      return [];
    },
    ...options,
  });
}

export function useBrandStyle(options = {}) {
  return useQuery({
    queryKey: qk.brandStyle(),
    queryFn: () => api.get('/brand/style').then(r => r.data),
    ...options,
  });
}

export function useDetailLevel(options = {}) {
  return useQuery({
    queryKey: qk.detailLevel(),
    queryFn: () => api.get('/brand/detail-level').then(r => r.data?.detail_level || 'standard'),
    ...options,
  });
}

export function useBrandOnboarded(options = {}) {
  return useQuery({
    queryKey: qk.brandOnboarded(),
    queryFn: () => api.get('/brand/onboarded').then(r => !!r.data?.onboarded),
    ...options,
  });
}

export function useSettings(options = {}) {
  return useQuery({
    queryKey: qk.settings(),
    queryFn: () => api.get('/settings').then(r => r.data),
    ...options,
  });
}

export function useWorkspace(options = {}) {
  return useQuery({
    queryKey: qk.workspace(),
    queryFn: () => api.get('/auth/me').then(r => r.data?.workspace || null),
    ...options,
  });
}

export function useFreshdeskConfig(options = {}) {
  return useQuery({
    queryKey: qk.freshdeskConfig(),
    queryFn: () => api.get('/freshdesk/config').then(r => r.data || {}),
    ...options,
  });
}

export function useProviderMode(options = {}) {
  return useQuery({
    queryKey: qk.providerMode(),
    queryFn: () => api.get('/provider-connections/mode').then(r => r.data || {}),
    ...options,
  });
}

export function useWhisperCheck(options = {}) {
  return useQuery({
    queryKey: qk.whisperCheck(),
    queryFn: () => api.get('/whisper-check').then(r => !!r.data?.installed),
    staleTime: 5 * 60_000,  // whisper install state changes rarely; cache 5 min
    ...options,
  });
}

export function useArticle(articleId, options = {}) {
  return useQuery({
    queryKey: qk.article(articleId),
    queryFn: () => api.get(`/articles/${articleId}`).then(r => r.data),
    enabled: !!articleId,
    ...options,
  });
}

export function useWallpapers(options = {}) {
  return useQuery({
    queryKey: qk.wallpapers(),
    queryFn: () => api.get('/wallpapers').then(r => Array.isArray(r.data) ? r.data : []),
    staleTime: 5 * 60_000,  // wallpaper list rarely changes; cache 5 min
    ...options,
  });
}

export function useFreshdeskFolders(options = {}) {
  return useQuery({
    queryKey: qk.freshdeskFolders(),
    queryFn: () => api.get('/freshdesk/folders').then(r => r.data || []),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useConfluenceSpaces(options = {}) {
  return useQuery({
    queryKey: qk.confluenceSpaces(),
    queryFn: () => api.get('/confluence/spaces').then(r => r.data || []),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useJiraTickets(params = {}, options = {}) {
  return useQuery({
    queryKey: qk.jiraTickets(params),
    queryFn: () => api.get('/jira/tickets', { params }).then(r => r.data || []),
    ...options,
  });
}

export function useJiraTicket(ticketId, options = {}) {
  return useQuery({
    queryKey: qk.jiraTicket(ticketId),
    queryFn: () => api.get(`/jira/tickets/${ticketId}`).then(r => r.data),
    enabled: !!ticketId,
    ...options,
  });
}

export function useSupportStatus(options = {}) {
  return useQuery({
    queryKey: qk.supportStatus(),
    queryFn: () => api.get('/support/status').then(r => r.data || {}),
    ...options,
  });
}

export function useSupportClusters(status = 'open', options = {}) {
  return useQuery({
    queryKey: qk.supportClusters(status),
    queryFn: () => api.get(`/support/clusters?status=${status}`).then(r => r.data || []),
    ...options,
  });
}

export function useSites(options = {}) {
  return useQuery({
    queryKey: qk.sites(),
    queryFn: () => api.get('/sites').then(r => r.data?.sites || []),
    ...options,
  });
}

export function useSiteMembers(siteId, options = {}) {
  return useQuery({
    queryKey: qk.siteMembers(siteId),
    queryFn: () => api.get(`/sites/${siteId}/members`).then(r => r.data?.members || []),
    enabled: !!siteId,
    ...options,
  });
}

export function useSearchGaps(days = 30, options = {}) {
  return useQuery({
    queryKey: qk.searchGaps(days),
    queryFn: () => api.get(`/sites/search-gaps?days=${days}`).then(r => r.data || { gaps: [] }),
    staleTime: 5 * 60_000,  // search-gaps refresh slowly — cache 5 min
    ...options,
  });
}

export function useOnboardingStatus(options = {}) {
  return useQuery({
    queryKey: qk.onboardingStatus(),
    queryFn: () => api.get('/onboarding/status').then(r => r.data || { step: 0, complete: false, use_case: null }),
    ...options,
  });
}

export function useReviews(projectSlug, options = {}) {
  return useQuery({
    queryKey: qk.reviews(projectSlug),
    queryFn: () => {
      const path = projectSlug ? `/reviews?project=${encodeURIComponent(projectSlug)}` : '/reviews';
      return api.get(path).then(r => r.data?.reviews || []);
    },
    ...options,
  });
}

export function useReviewProjects(options = {}) {
  return useQuery({
    queryKey: qk.reviewProjects(),
    queryFn: () => api.get('/review-projects').then(r => r.data?.projects || []),
    ...options,
  });
}

export function useReviewProject(slug, options = {}) {
  return useQuery({
    queryKey: qk.reviewProject(slug),
    queryFn: () => api.get(`/review-projects/${encodeURIComponent(slug)}`).then(r => r.data),
    enabled: !!slug,
    ...options,
  });
}


// ── Plans (spec §1.3.3) ──────────────────────────────────────────────────────
// GET /api/plan is truthful whether or not PLANS_ENFORCED is on, so the
// banners can ship ahead of the switch.

export function usePlan(options = {}) {
  return useQuery({
    queryKey: qk.plan(),
    queryFn: async () => (await api.get('/plan')).data,
    staleTime: 60_000,
    ...options,
  });
}

export function useAiKeyRouting(options = {}) {
  return useQuery({
    queryKey: qk.aiKeyRouting(),
    queryFn: async () => (await api.get('/ai-keys/routing')).data,
    staleTime: 60_000,
    ...options,
  });
}
